import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  createUserWithEmailAndPassword,
  getMultiFactorResolver,
  multiFactor,
  signInWithEmailAndPassword,
  type MultiFactorError,
  type MultiFactorInfo,
  type MultiFactorResolver,
} from 'firebase/auth';
import { api, ApiError } from '../../lib/api';
import { getFirebaseAuth } from '../../lib/firebase';
import { useSession } from '../../lib/useSession';
import type { Membership } from '../../lib/sessionContext';
import { Banner } from '../../components/Banner';
import { Button } from '../../components/Button';
import { Input } from '../../components/Input';
import { AuthShell } from '../auth/AuthShell';
import { authErrorMessage, NO_TOTP_FACTOR, resolveTotpSignIn, totpHint } from '../auth/mfa';
import { ROLE_LABEL } from '../members/roles';
import { nextPathAfterAccept } from './next';
import { BRAND } from '../../brand';

type Mode = 'login' | 'signup';

export function InvitationPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const { status, user, setActiveOrg, refreshMemberships, logout } = useSession();
  const navigate = useNavigate();

  const [mode, setMode] = useState<Mode>('signup');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [authError, setAuthError] = useState<string | null>(null);
  const [authNotice, setAuthNotice] = useState<string | null>(null);
  // Cuenta con segundo factor: el código se pide en esta misma tarjeta para no perder el token (BUG-51).
  const [mfa, setMfa] = useState<{ resolver: MultiFactorResolver; hint: MultiFactorInfo | null } | null>(null);
  const [code, setCode] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [problem, setProblem] = useState<{ text: string; mismatch?: boolean } | null>(null);

  if (!token || token.length < 20) {
    return (
      <AuthShell title="Invitación no válida">
        <Banner tone="error">El enlace está incompleto. Pide a quien te invitó que te envíe una invitación nueva.</Banner>
        <Link to="/login">Ir a iniciar sesión</Link>
      </AuthShell>
    );
  }

  async function onAuth(e: FormEvent) {
    e.preventDefault();
    setAuthError(null);
    setAuthNotice(null);
    if (mode === 'signup') {
      if (password.length < 10) {
        setAuthError('La contraseña debe tener al menos 10 caracteres.');
        return;
      }
      if (password !== repeat) {
        setAuthError('Las contraseñas no coinciden.');
        return;
      }
    }
    setAuthLoading(true);
    try {
      const auth = getFirebaseAuth();
      if (mode === 'signup') await createUserWithEmailAndPassword(auth, email.trim(), password);
      else await signInWithEmailAndPassword(auth, email.trim(), password);
    } catch (err) {
      const code = (err as { code?: string }).code;
      if (code === 'auth/email-already-in-use') {
        setMode('login');
        setRepeat('');
        setAuthNotice(`Ese email ya tiene cuenta en ${BRAND}. Entra con tu contraseña para aceptar la invitación.`);
      } else if (code === 'auth/multi-factor-auth-required') {
        const resolver = getMultiFactorResolver(getFirebaseAuth(), err as MultiFactorError);
        setCode('');
        setMfa({ resolver, hint: totpHint(resolver) });
      } else if (code === 'auth/weak-password') {
        setAuthError('La contraseña es muy débil. Usa al menos 10 caracteres combinando letras y números.');
      } else {
        setAuthError(authErrorMessage(code));
      }
    } finally {
      setAuthLoading(false);
    }
  }

  async function onCode(e: FormEvent) {
    e.preventDefault();
    if (!mfa?.hint) return;
    setAuthError(null);
    setAuthLoading(true);
    try {
      await resolveTotpSignIn(mfa.resolver, mfa.hint, code);
      // La sesión pasa a signed-in y la tarjeta muestra «Aceptar invitación» con el mismo token.
      setMfa(null);
    } catch (err) {
      setAuthError(authErrorMessage((err as { code?: string }).code));
    } finally {
      setAuthLoading(false);
    }
  }

  function cancelCode() {
    setMfa(null);
    setCode('');
    setAuthError(null);
  }

  async function switchAccount() {
    setProblem(null);
    await logout();
  }

  async function accept() {
    setProblem(null);
    setAccepting(true);
    try {
      const membership = await api.post<Membership>('/v1/invitations/accept', { token });
      await refreshMemberships();
      setActiveOrg(membership.orgId);
      const current = getFirebaseAuth().currentUser;
      const hasFactor = current ? multiFactor(current).enrolledFactors.length > 0 : false;
      navigate(nextPathAfterAccept(membership.role, hasFactor), { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.code === 'already_member') {
        navigate('/', { replace: true });
        return;
      }
      if (err instanceof ApiError && err.code === 'invitation_email_mismatch') {
        setProblem({
          text: `La invitación es para otro email. Entraste como ${user?.email ?? 'otra cuenta'}: sal y entra con el email al que llegó la invitación.`,
          mismatch: true,
        });
      } else if (err instanceof ApiError && (err.status === 410 || err.code === 'invitation_invalid')) {
        setProblem({ text: 'La invitación venció o ya se usó. Pide a quien te invitó que te envíe una nueva.' });
      } else {
        setProblem({ text: err instanceof ApiError ? err.message : 'No pudimos aceptar la invitación. Intenta de nuevo.' });
      }
    } finally {
      setAccepting(false);
    }
  }

  if (status === 'loading') {
    return (
      <AuthShell title={`Invitación a ${BRAND}`}>
        <p className="text-sm text-ink-muted" role="status">Cargando…</p>
      </AuthShell>
    );
  }

  if (status === 'signed-in' && user) {
    return (
      <AuthShell title="Únete a tu equipo" subtitle={`Entraste como ${user.email ?? 'tu cuenta'}.`}>
        <div className="space-y-4" aria-live="polite">
          {problem && <Banner tone="error">{problem.text}</Banner>}
          <p className="text-sm text-ink">
            Al aceptar quedarás como miembro de la organización con el rol que te asignaron. Si es {ROLE_LABEL.owner} o {ROLE_LABEL.admin}, te pediremos configurar el segundo factor.
          </p>
          <Button block size="lg" loading={accepting} onClick={() => void accept()}>Aceptar invitación</Button>
          {problem?.mismatch ? (
            <Button variant="secondary" block onClick={() => void switchAccount()}>Salir y entrar con otra cuenta</Button>
          ) : (
            <Button variant="ghost" block onClick={() => void switchAccount()}>¿No eres tú? Entra con otra cuenta</Button>
          )}
        </div>
      </AuthShell>
    );
  }

  if (mfa) {
    return (
      <AuthShell title="Código de verificación" subtitle="Tu cuenta tiene segundo factor. Ingresa el código de 6 dígitos de tu app de autenticación.">
        {!mfa.hint ? (
          <Banner tone="error">{NO_TOTP_FACTOR}</Banner>
        ) : (
          <form onSubmit={onCode} className="space-y-4" noValidate>
            {authError && <Banner tone="error">{authError}</Banner>}
            <Input
              label="Código"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={7}
              mono
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className="text-center text-xl"
            />
            <Button type="submit" block size="lg" loading={authLoading} disabled={code.replace(/\s/g, '').length !== 6}>
              Verificar y continuar
            </Button>
          </form>
        )}
        <Button variant="ghost" block onClick={cancelCode}>Volver</Button>
      </AuthShell>
    );
  }

  return (
    <AuthShell title={`Invitación a ${BRAND}`} subtitle="Crea tu cuenta o inicia sesión con el email al que llegó la invitación.">
      <div className="grid grid-cols-2 gap-1 rounded-md bg-mist p-1" role="tablist" aria-label="Cuenta">
        {(['signup', 'login'] as const).map((m) => (
          <button
            key={m}
            type="button"
            role="tab"
            aria-selected={mode === m}
            onClick={() => { setMode(m); setAuthError(null); setAuthNotice(null); }}
            className={`rounded-sm px-3 py-2 text-sm font-semibold ${mode === m ? 'bg-white text-ink shadow-1' : 'text-ink-muted'}`}
          >
            {m === 'signup' ? 'Crear cuenta' : 'Ya tengo cuenta'}
          </button>
        ))}
      </div>
      <form onSubmit={onAuth} className="space-y-4" noValidate>
        {authError && <Banner tone="error">{authError}</Banner>}
        {authNotice && !authError && <Banner tone="info">{authNotice}</Banner>}
        <Input label="Email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        <Input
          label="Contraseña"
          type="password"
          autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          hint={mode === 'signup' ? 'Al menos 10 caracteres.' : undefined}
        />
        {mode === 'signup' && (
          <Input label="Repite la contraseña" type="password" autoComplete="new-password" value={repeat} onChange={(e) => setRepeat(e.target.value)} />
        )}
        <Button type="submit" block size="lg" loading={authLoading} disabled={!email || !password}>
          {mode === 'signup' ? 'Crear cuenta y continuar' : 'Entrar y continuar'}
        </Button>
      </form>
    </AuthShell>
  );
}
